import { Flex, Input } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { MaskedInput } from '../../../components/MaskedInput'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import { FormLeadService } from '../../../services/FormLead/FormLeadService'
import { LeadSourceField } from './LeadSourceField'
import { eventFormat, IFormLead, isEventLead, IFormLeadStatus, statusTranslations } from './models'

interface IProps {
  isOpen: boolean
  onClose: () => void
  lead: IFormLead | null
  sellers: string[]
  canManageSources: boolean
}

export function FormLeadsEditModal({ isOpen, onClose, lead, sellers, canManageSources }: IProps) {
  const service = new FormLeadService()
  const toast = useAppToast()
  const [areas, setAreas] = useState<string[]>([])
  const [courses, setCourses] = useState<string[]>([])
  const [form, setForm] = useState<IFormLead>({
    id: 0,
    name: '',
    email: '',
    phone: '',
    format: '',
    area: '',
    course: '',
    source: '',
    seller_name: '',
    status: 'Sem Contato',
    utm_campaign: '',
    utm_source: '',
    utm_medium: '',
    utm_content: '',
    utm_term: '',
    created_at: ''
  })
  const isEvent = isEventLead(form.format)

  const loadAreas = async () => {
    setAreas(await service.getAreas(form.format))
  }

  const loadCourses = async () => {
    setCourses(await service.getCourses(form.area))
  }

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()

    service
      .update(form.id, form)
      .then(() => {
        toast.success('Registro atualizado com sucesso')

        onClose()
      })
      .catch(() => {
        toast.error('Erro ao atualizar registro')
      })
  }

  useEffect(() => {
    if (lead) {
      setForm({ ...lead, format: lead.format.toLowerCase() })
    }
  }, [lead])

  useEffect(() => {
    if (isEvent) return

    loadAreas()
  }, [form.format])

  useEffect(() => {
    if (isEvent) return

    loadCourses()
  }, [form.area])

  return (
    lead && (
      <>
        <FormModal isOpen={isOpen} onClose={onClose} title="Contato via formulário" onSubmit={handleSave} size="wide">
          <Flex direction="column">
            <FormField label="Nome" isRequired>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </FormField>
            <FormField label="E-mail" isRequired mt={4}>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </FormField>
            <FormField label="Telefone" mt={4}>
              <MaskedInput mask="phone" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
            </FormField>
            <FormField label="Status" mt={4}>
              <FormSelect
                value={form.status}
                onChange={(value) => setForm({ ...form, status: value as IFormLeadStatus })}
                options={[
                  { value: 'Sem Contato', label: statusTranslations['Sem Contato'] },
                  { value: 'Qualificado', label: statusTranslations.Qualificado },
                  { value: 'Desqualificado', label: statusTranslations.Desqualificado }
                ]}
                hideEmptyOption
              />
            </FormField>
            <FormField label="Formato" mt={4}>
              <FormSelect
                value={form.format}
                onChange={(value) => setForm({ ...form, format: value })}
                options={[
                  { value: 'online', label: 'Online' },
                  { value: 'online ao vivo', label: 'Online Ao Vivo' },
                  { value: 'presencial', label: 'Presencial' },
                  ...(isEvent ? [{ value: eventFormat.toLowerCase(), label: 'Evento' }] : [])
                ]}
                hideEmptyOption
              />
            </FormField>
            {!isEvent && (
              <>
                <FormField label="Área" mt={4}>
                  <FormSelect
                    value={form.area}
                    onChange={(value) => setForm({ ...form, area: value })}
                    options={areas.map((area) => ({ value: area, label: area }))}
                    hideEmptyOption
                  />
                </FormField>
                <FormField label="Curso" mt={4}>
                  <FormSelect
                    value={form.course}
                    onChange={(value) => setForm({ ...form, course: value })}
                    options={courses.map((course) => ({ value: course, label: course }))}
                    hideEmptyOption
                  />
                </FormField>
              </>
            )}
            <LeadSourceField
              mt={4}
              value={form.source}
              onChange={(source) => setForm((prev) => ({ ...prev, source }))}
              canManage={canManageSources}
              extraValue={lead.source}
            />
            <FormField label="Responsável" mt={4}>
              <FormSelect
                value={form.seller_name}
                onChange={(value) => setForm({ ...form, seller_name: value })}
                options={sellers.map((seller) => ({ value: seller, label: seller }))}
                hideEmptyOption
              />
            </FormField>
            <FormField label="UTM Campaign" mt={4}>
              <Input value={form.utm_campaign} readOnly />
            </FormField>
            <FormField label="UTM Source" mt={4}>
              <Input value={form.utm_source} readOnly />
            </FormField>
            <FormField label="UTM Medium" mt={4}>
              <Input value={form.utm_medium} readOnly />
            </FormField>
            <FormField label="UTM Content" mt={4}>
              <Input value={form.utm_content} readOnly />
            </FormField>
            <FormField label="UTM Term" mt={4}>
              <Input value={form.utm_term} readOnly />
            </FormField>
          </Flex>
        </FormModal>
      </>
    )
  )
}
